import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authApi, leaveTypesApi } from '../../services/api';
import { userAvatarSrc } from '../../utils/avatarUrl';
import { formatDateDMY, formatDateTimeDMY } from '../../utils/dateFormat';
import { formatMonthYearIST } from '../../utils/timezoneDisplay';
import toast from 'react-hot-toast';
import LeaveBalanceSummary from '../../components/Leave/LeaveBalanceSummary';
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlinePhotograph,
  HiOutlineClipboardList,
  HiOutlineClock,
  HiOutlinePencil,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineExclamation,
  HiOutlineKey,
} from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import ForgotPasswordForm from '../../components/Auth/ForgotPasswordForm';

const TABS = [
  { id: 'profile', label: 'Profile', icon: HiOutlineUser },
  { id: 'leave', label: 'Leave requests', short: 'Leave', icon: HiOutlineClipboardList },
  { id: 'attendance', label: 'Attendance', short: 'Attend.', icon: HiOutlineClock }
];

function shiftCalendarMonth(year, month1, delta) {
  const dt = new Date(year, month1 - 1 + delta, 1);
  return { year: dt.getFullYear(), month: dt.getMonth() + 1 };
}

function monthLabel(year, month1) {
  return formatMonthYearIST(new Date(Date.UTC(year, month1 - 1, 15, 12, 0, 0)));
}

function statusBadgeClass(status) {
  if (status === 'approved') return 'bg-emerald-500/10 text-emerald-800 ring-1 ring-emerald-500/25';
  if (status === 'rejected') return 'bg-rose-500/10 text-rose-800 ring-1 ring-rose-500/25';
  return 'bg-amber-500/10 text-amber-900 ring-1 ring-amber-500/25';
}

function attendanceStatusStyle(status) {
  switch (status) {
    case 'present':
      return 'bg-emerald-500/10 text-emerald-800';
    case 'absent':
      return 'bg-rose-500/10 text-rose-800';
    case 'leave':
      return 'bg-violet-500/10 text-violet-800';
    case 'half_day':
      return 'bg-amber-500/10 text-amber-900';
    case 'holiday':
      return 'bg-sky-500/10 text-sky-800';
    default:
      return 'bg-neutral-100 text-neutral-700';
  }
}

const Profile = () => {
  const { user, updateProfile, refreshUser } = useAuth();
  const { isAdmin } = usePermissions();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const fileInputRef = useRef(null);
  const now = new Date();
  const [activeTab, setActiveTab] = useState('profile');
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '', date_of_birth: '' });
  const [leaveTypes, setLeaveTypes] = useState([]);

  const [attendance, setAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveListLoading, setLeaveListLoading] = useState(false);

  const [leaveForm, setLeaveForm] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: '',
    is_half_day: false,
  });
  const [monthlyLeaveBalance, setMonthlyLeaveBalance] = useState(null);
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [editingLeaveId, setEditingLeaveId] = useState(null);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const [meRes, ltRes] = await Promise.all([
        authApi.getProfile(),
        leaveTypesApi.getActive().catch(() => ({ data: { data: [] } }))
      ]);
      const me = meRes.data.data;
      setProfile(me);
      setForm({
        full_name: me.full_name || '',
        phone: me.phone || '',
        date_of_birth: me.date_of_birth || ''
      });
      setLeaveTypes(ltRes.data?.data || []);
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const loadAttendance = useCallback(async () => {
    setAttendanceLoading(true);
    try {
      const res = await authApi.getMyAttendance({
        month: filterMonth,
        year: filterYear
      });
      setAttendance(res.data.data || []);
    } catch {
      toast.error('Failed to load attendance');
      setAttendance([]);
    } finally {
      setAttendanceLoading(false);
    }
  }, [filterYear, filterMonth]);

  const loadLeaveRequests = useCallback(async () => {
    setLeaveListLoading(true);
    try {
      const [reqRes, balRes] = await Promise.all([
        authApi.getMyLeaveRequests({ month: filterMonth, year: filterYear }),
        authApi.getMyLeaveBalance({ month: filterMonth, year: filterYear }).catch(() => ({ data: { data: null } })),
      ]);
      setLeaveRequests(reqRes.data.data || []);
      setMonthlyLeaveBalance(balRes.data.data || null);
    } catch {
      toast.error('Failed to load leave requests');
      setLeaveRequests([]);
      setMonthlyLeaveBalance(null);
    } finally {
      setLeaveListLoading(false);
    }
  }, [filterYear, filterMonth]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (activeTab !== 'attendance') return;
    loadAttendance();
  }, [activeTab, loadAttendance]);

  useEffect(() => {
    if (activeTab !== 'leave') return;
    loadLeaveRequests();
  }, [activeTab, loadLeaveRequests]);

  const goPrevMonth = () => {
    const { year, month } = shiftCalendarMonth(filterYear, filterMonth, -1);
    setFilterYear(year);
    setFilterMonth(month);
  };

  const goNextMonth = () => {
    const { year, month } = shiftCalendarMonth(filterYear, filterMonth, 1);
    setFilterYear(year);
    setFilterMonth(month);
  };

  const goThisMonth = () => {
    const t = new Date();
    setFilterYear(t.getFullYear());
    setFilterMonth(t.getMonth() + 1);
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({
        full_name: form.full_name,
        phone: form.phone || undefined,
        date_of_birth: form.date_of_birth || null
      });
      toast.success('Profile updated');
      await loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await authApi.uploadProfileImage(fd);
      await refreshUser();
      toast.success('Photo updated');
      await loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason.trim()) {
      toast.error('Please fill start date, end date, and reason');
      return;
    }
    if (monthlyLeaveBalance && !leaveForm.leave_type) {
      toast.error('Please select a leave type');
      return;
    }
    setSubmittingLeave(true);
    try {
      const payload = {
        start_date: String(leaveForm.start_date).slice(0, 10),
        end_date: String(leaveForm.end_date).slice(0, 10),
        reason: leaveForm.reason.trim(),
        leave_type: leaveForm.leave_type || undefined,
        is_half_day: leaveForm.is_half_day || undefined,
      };

      if (editingLeaveId) {
        await authApi.updateMyLeaveRequest(editingLeaveId, payload);
        toast.success('Leave request updated');
      } else {
        await authApi.createLeaveRequest(payload);
        toast.success('Leave request sent');
      }

      setLeaveForm({ start_date: '', end_date: '', reason: '', leave_type: '', is_half_day: false });
      setEditingLeaveId(null);
      await loadLeaveRequests();
      window.dispatchEvent(new CustomEvent('pendingLeaveRequestsChanged'));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Request failed');
    } finally {
      setSubmittingLeave(false);
    }
  };

  const handleStartLeaveEdit = (request) => {
    if (request.status !== 'pending') return;
    setEditingLeaveId(request.id);
    setLeaveForm({
      start_date: request.start_date || '',
      end_date: request.end_date || '',
      reason: request.reason || '',
      leave_type: request.leave_type || '',
      is_half_day: request.is_half_day === 1 || request.is_half_day === true,
    });
  };

  const handleCancelLeaveEdit = () => {
    setEditingLeaveId(null);
    setLeaveForm({ start_date: '', end_date: '', reason: '', leave_type: '', is_half_day: false });
  };

  const displayUser = profile || user;
  const img = userAvatarSrc(displayUser);
  const isInNoticePeriod = Boolean(profile?.is_in_notice_period);

  const isMonthlyLedgerType = (lt) =>
    lt.uses_monthly_ledger === 1 || lt.uses_monthly_ledger === true;

  const isHalfDayCompanionType = (lt) =>
    (lt.allows_half_day === 1 || lt.allows_half_day === true) &&
    !isMonthlyLedgerType(lt);

  const monthlyLedgerType = leaveTypes.find(isMonthlyLedgerType);
  const halfDayCompanionType = leaveTypes.find(isHalfDayCompanionType);

  const requestLeaveTypes = React.useMemo(() => {
    if (monthlyLedgerType && halfDayCompanionType) {
      return leaveTypes.filter(
        (lt) => lt.name === monthlyLedgerType.name || lt.name === halfDayCompanionType.name
      );
    }
    return leaveTypes;
  }, [leaveTypes, monthlyLedgerType, halfDayCompanionType]);

  const isHalfDayLeaveSelected = leaveForm.is_half_day;

  const handleLeaveTypeChange = (leaveTypeName) => {
    const nextType = leaveTypes.find((lt) => lt.name === leaveTypeName);
    const halfDayType = nextType && isHalfDayCompanionType(nextType);
    setLeaveForm((f) => ({
      ...f,
      leave_type: leaveTypeName,
      is_half_day: Boolean(halfDayType),
      end_date: halfDayType ? f.start_date : f.end_date,
    }));
  };

  const attendanceSummary = React.useMemo(() => {
    let present = 0;
    let absent = 0;
    let leave = 0;
    let half = 0;
    let holiday = 0;
    attendance.forEach((row) => {
      if (row.status === 'present') present += 1;
      else if (row.status === 'absent') absent += 1;
      else if (row.status === 'leave') leave += 1;
      else if (row.status === 'half_day') half += 1;
      else if (row.status === 'holiday') holiday += 1;
    });
    return { present, absent, leave, half, holiday, total: attendance.length };
  }, [attendance]);

  const isThisMonth =
    filterYear === now.getFullYear() && filterMonth === now.getMonth() + 1;

    if (profileLoading && !displayUser) {
    return (
      <div className="w-full min-h-[40vh] flex items-center justify-center bg-neutral-100/80 -mx-4 lg:-mx-6">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary-600 border-t-transparent" />
          <p className="text-xs text-neutral-500">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-full">
      {/* Full-bleed header strip */}
      <div className="w-full border-b border-neutral-200/90 bg-gradient-to-r ">
        <div className="w-full px-3 sm:px-4 lg:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-base sm:text-lg font-bold tracking-tight">My profile</h1>
            <p className="text-[11px] sm:text-xs text-neutral-400 mt-0.5">Account, leave requests, attendance</p>
          </div>
        </div>
      </div>

      {/* Tabs — full width, minimal padding */}
      <div className="w-full bg-white border-b border-neutral-200 shadow-sm">
        <nav
          className="w-full flex px-2 sm:px-3 lg:px-4 gap-0"
          aria-label="Profile sections"
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 text-xs sm:text-sm font-semibold border-b-2 transition-colors ${
                  active
                    ? 'border-primary-600 text-primary-700 bg-primary-50/40'
                    : 'border-transparent text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
                }`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-primary-600' : ''}`} />
                <span className="sm:hidden truncate">{tab.short || tab.label}</span>
                <span className="hidden sm:inline truncate">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="w-full px-3 sm:px-4 lg:px-6 py-3">
        {activeTab === 'profile' && (
          <div className="w-full bg-white border border-neutral-200/90 rounded-lg shadow-sm overflow-hidden">
            <div className="grid lg:grid-cols-[auto_1fr] divide-y lg:divide-y-0 lg:divide-x divide-neutral-100">
              <div className="p-4 lg:p-5 flex flex-row lg:flex-col items-center lg:items-stretch gap-4 lg:gap-3 bg-neutral-50/50 lg:min-w-[200px]">
                <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-lg ring-2 ring-white shadow overflow-hidden bg-primary-100 shrink-0">
                  {img ? (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-primary-700 text-2xl font-bold">
                      {displayUser?.full_name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                <div className="flex-1 min-w-0 lg:text-center">
                  <p className="font-bold text-neutral-900 text-sm lg:text-base truncate">{displayUser?.full_name}</p>
                  <p className="text-[11px] text-neutral-500 capitalize">{displayUser?.role}</p>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 w-full lg:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-900 text-white text-xs font-medium hover:bg-neutral-800 disabled:opacity-50"
                  >
                    <HiOutlinePhotograph className="w-3.5 h-3.5" />
                    {uploading ? '…' : 'Change photo'}
                  </button>
                </div>
              </div>

              <div className="p-4 lg:p-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2 p-3 rounded-md border border-neutral-100 bg-neutral-50/60">
                    <HiOutlineMail className="w-4 h-4 text-primary-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Email</p>
                      <p className="text-xs font-medium text-neutral-900 break-all">{displayUser?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-md border border-neutral-100 bg-neutral-50/60">
                    <HiOutlineUser className="w-4 h-4 text-primary-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Role</p>
                      <p className="text-xs font-medium text-neutral-900 capitalize">{displayUser?.role}</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSaveDetails} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">Full name</label>
                    <input
                      className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      value={form.full_name}
                      onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">Phone</label>
                    <div className="relative">
                      <HiOutlinePhone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                      <input
                        className="w-full rounded-md border border-neutral-200 bg-white pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="Phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">Date of birth</label>
                    <div className="relative">
                      <HiOutlineCalendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                      <input
                        type="date"
                        className="w-full rounded-md border border-neutral-200 bg-white pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                        value={form.date_of_birth || ''}
                        onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3 flex">
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>

                {isAdmin && (
                  <div className="mt-6 pt-6 border-t border-neutral-200">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <HiOutlineKey className="w-4 h-4 text-primary-600" />
                        <h3 className="text-sm font-bold text-neutral-900">Reset password</h3>
                      </div>
                      {!showPasswordReset && (
                        <button
                          type="button"
                          onClick={() => setShowPasswordReset(true)}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    {showPasswordReset ? (
                      <>
                        <ForgotPasswordForm
                          initialEmail={displayUser?.email || ''}
                          onSuccess={() => setShowPasswordReset(false)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordReset(false)}
                          className="mt-3 text-sm text-neutral-500 hover:text-neutral-700"
                        >
                          Hide
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-neutral-500">
                        Use email verification to set a new password for your admin account.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'leave' && (
          <div className="w-full space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-neutral-200/90 rounded-lg px-3 py-2">
              <div>
                <h2 className="text-sm font-bold text-neutral-900">Leave requests</h2>
                <p className="text-[11px] text-neutral-500">Overlapping {monthLabel(filterYear, filterMonth)}</p>
              </div>
              <MonthNavigator
                label={monthLabel(filterYear, filterMonth)}
                isThisMonth={isThisMonth}
                onPrev={goPrevMonth}
                onNext={goNextMonth}
                onToday={goThisMonth}
              />
            </div>

            {monthlyLeaveBalance && (
              <LeaveBalanceSummary balance={monthlyLeaveBalance} availableOnly />
            )}

            {isInNoticePeriod && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                <p className="font-semibold">2-month notice period</p>
                <p className="mt-0.5 text-xs sm:text-sm">
                  You are in your notice period
                  {profile?.notice_period_start && profile?.notice_period_end
                    ? ` (${formatDateDMY(profile.notice_period_start)} to ${formatDateDMY(profile.notice_period_end)})`
                    : profile?.resign_date
                      ? ` (from resign date ${formatDateDMY(profile.resign_date)})`
                      : ''}
                  . You cannot add or edit leave requests during this time.
                </p>
              </div>
            )}

            <div className="grid xl:grid-cols-2 gap-3 w-full">
              <div className="bg-white border border-neutral-200/90 rounded-lg p-3 sm:p-4 shadow-sm">
                <h3 className="text-xs font-bold text-neutral-800 uppercase tracking-wide mb-3">
                  {editingLeaveId ? 'Edit request' : 'New request'}
                </h3>
                {isInNoticePeriod ? (
                  <p className="text-sm text-neutral-600">
                    Leave requests are disabled during your 2-month notice period.
                  </p>
                ) : (
                <form onSubmit={handleLeaveSubmit} className="grid gap-2.5 sm:grid-cols-2">
                  <div>
                    <label className="block text-[10px] font-semibold text-neutral-500 mb-1">From</label>
                    <input
                      type="date"
                      required
                      className="w-full rounded-md border border-neutral-200 px-2.5 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                      value={leaveForm.start_date}
                      onChange={(e) => {
                        const start = e.target.value;
                        setLeaveForm((f) => ({
                          ...f,
                          start_date: start,
                          end_date: f.is_half_day ? start : f.end_date,
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-neutral-500 mb-1">To</label>
                    <input
                      type="date"
                      required
                      disabled={isHalfDayLeaveSelected}
                      className="w-full rounded-md border border-neutral-200 px-2.5 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-neutral-50 disabled:text-neutral-500"
                      value={isHalfDayLeaveSelected ? leaveForm.start_date : leaveForm.end_date}
                      onChange={(e) => setLeaveForm((f) => ({ ...f, end_date: e.target.value }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-semibold text-neutral-500 mb-1">
                      Leave type{monthlyLeaveBalance ? ' *' : ' (optional)'}
                    </label>
                    <select
                      className="w-full rounded-md border border-neutral-200 px-2.5 py-2 text-sm bg-white focus:ring-1 focus:ring-primary-500 outline-none"
                      value={leaveForm.leave_type}
                      onChange={(e) => handleLeaveTypeChange(e.target.value)}
                      required={Boolean(monthlyLeaveBalance)}
                    >
                      <option value="">—</option>
                      {requestLeaveTypes.map((lt) => (
                        <option key={lt.id} value={lt.name}>
                          {lt.name}
                          {isHalfDayCompanionType(lt) ? ' (0.5 day)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-semibold text-neutral-500 mb-1">Reason</label>
                    <textarea
                      required
                      rows={3}
                      className="w-full rounded-md border border-neutral-200 px-2.5 py-2 text-sm resize-none focus:ring-1 focus:ring-primary-500 outline-none"
                      placeholder="Reason for leave"
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={submittingLeave}
                        className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                      >
                        {submittingLeave ? (editingLeaveId ? 'Saving…' : 'Submitting…') : (editingLeaveId ? 'Save changes' : 'Submit')}
                      </button>
                      {editingLeaveId && (
                        <button
                          type="button"
                          onClick={handleCancelLeaveEdit}
                          className="px-4 py-2 rounded-md border border-neutral-300 text-neutral-700 text-sm font-semibold hover:bg-neutral-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </form>
                )}
              </div>

              <div className="bg-white border border-neutral-200/90 rounded-lg p-3 sm:p-4 shadow-sm min-h-[200px]">
                <h3 className="text-xs font-bold text-neutral-800 uppercase tracking-wide mb-2">
                  Your requests <span className="font-normal text-neutral-400">· {monthLabel(filterYear, filterMonth)}</span>
                </h3>
                {leaveListLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
                  </div>
                ) : leaveRequests.length === 0 ? (
                  <div className="py-10 text-center border border-dashed border-neutral-200 rounded-md">
                    <HiOutlineExclamation className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-xs font-medium text-neutral-600">No requests this month</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-neutral-100 max-h-[min(60vh,520px)] overflow-y-auto">
                    {leaveRequests.map((r) => (
                      <li key={r.id} className="py-2.5 first:pt-0 flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-neutral-900 font-mono tabular-nums">
                            {formatDateDMY(r.start_date)} <span className="text-neutral-400 font-sans">→</span>{' '}
                            {formatDateDMY(r.end_date)}
                          </p>
                          {r.leave_type && (
                            <p className="text-[11px] text-neutral-500 mt-0.5 flex flex-wrap items-center gap-1">
                              <span>{r.leave_type}</span>
                              {(r.is_half_day === 1 || r.is_half_day === true) && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                  Half day
                                </span>
                              )}
                            </p>
                          )}
                          <p className="text-xs text-neutral-600 mt-1 line-clamp-2">{r.reason}</p>
                          <p className="text-[11px] text-neutral-500 mt-1 font-mono tabular-nums">
                            Submitted: {formatDateTimeDMY(r.created_at)}
                          </p>
                          {r.reviewed_at && (
                            <p className="text-[11px] text-neutral-400 mt-0.5 font-mono tabular-nums">
                              Reviewed: {formatDateTimeDMY(r.reviewed_at)}
                            </p>
                          )}
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded capitalize ${statusBadgeClass(r.status)}`}
                        >
                          {r.status}
                        </span>
                        {r.status === 'pending' && !isInNoticePeriod && (
                          <button
                            type="button"
                            onClick={() => handleStartLeaveEdit(r)}
                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800 hover:underline"
                          >
                            <HiOutlinePencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="w-full space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-neutral-200/90 rounded-lg px-3 py-2">
              <div>
                <h2 className="text-sm font-bold text-neutral-900">Attendance</h2>
                <p className="text-[11px] text-neutral-500">{monthLabel(filterYear, filterMonth)}</p>
              </div>
              <MonthNavigator
                label={monthLabel(filterYear, filterMonth)}
                isThisMonth={isThisMonth}
                onPrev={goPrevMonth}
                onNext={goNextMonth}
                onToday={goThisMonth}
              />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <SummaryPill icon={HiOutlineCheckCircle} label="Present" value={attendanceSummary.present} tone="emerald" />
              <SummaryPill icon={HiOutlineXCircle} label="Absent" value={attendanceSummary.absent} tone="rose" />
              <SummaryPill icon={HiOutlineClipboardList} label="Leave" value={attendanceSummary.leave} tone="violet" />
              <SummaryPill
                icon={HiOutlineClock}
                label="Days logged"
                value={attendanceSummary.total}
                tone="slate"
                sub={
                  attendanceSummary.half + attendanceSummary.holiday > 0
                    ? `${attendanceSummary.half + attendanceSummary.holiday} half/holiday`
                    : null
                }
              />
            </div>

            <div className="w-full bg-white border border-neutral-200/90 rounded-lg overflow-hidden shadow-sm">
              {attendanceLoading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
                </div>
              ) : attendance.length === 0 ? (
                <div className="py-12 text-center">
                  <HiOutlineClock className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                  <p className="text-xs font-medium text-neutral-600">No records</p>
                </div>
              ) : (
                <>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead>
                        <tr className="bg-neutral-50 border-b border-neutral-200 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Check in</th>
                          <th className="px-3 py-2">Check out</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {attendance.map((row) => (
                          <tr key={row.id} className="hover:bg-neutral-50/80">
                            <td className="px-3 py-2 font-mono text-xs text-neutral-900 tabular-nums whitespace-nowrap">
                              {formatDateDMY(row.attendance_date)}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-neutral-700 tabular-nums">{row.check_in || '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs text-neutral-700 tabular-nums">{row.check_out || '—'}</td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded ${attendanceStatusStyle(row.status)}`}
                              >
                                {row.status_label}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden divide-y divide-neutral-100">
                    {attendance.map((row) => (
                      <div key={row.id} className="px-3 py-2 flex items-center justify-between gap-2">
                        <div>
                          <p className="font-mono text-xs font-semibold text-neutral-900 tabular-nums">
                            {formatDateDMY(row.attendance_date)}
                          </p>
                          <p className="text-[10px] font-mono text-neutral-500 tabular-nums mt-0.5">
                            {row.check_in || '—'} – {row.check_out || '—'}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 ${attendanceStatusStyle(row.status)}`}
                        >
                          {row.status_label}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function MonthNavigator({ label, isThisMonth, onPrev, onNext, onToday }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <div className="flex items-center rounded-md border border-neutral-200 bg-neutral-50 overflow-hidden text-xs">
        <button
          type="button"
          onClick={onPrev}
          className="p-1.5 text-neutral-600 hover:bg-white"
          aria-label="Previous month"
        >
          <HiChevronLeft className="w-4 h-4" />
        </button>
        <span className="px-2 py-1 font-semibold text-neutral-800 min-w-[7.5rem] text-center border-x border-neutral-200 bg-white">
          {label}
        </span>
        <button
          type="button"
          onClick={onNext}
          className="p-1.5 text-neutral-600 hover:bg-white"
          aria-label="Next month"
        >
          <HiChevronRight className="w-4 h-4" />
        </button>
      </div>
      {!isThisMonth && (
        <button
          type="button"
          onClick={onToday}
          className="text-[11px] font-semibold text-primary-600 hover:underline px-1"
        >
          This month
        </button>
      )}
    </div>
  );
}

function SummaryPill({ icon: Icon, label, value, tone, sub }) {
  const ring =
    tone === 'emerald'
      ? 'border-emerald-200/80 bg-emerald-50/40'
      : tone === 'rose'
        ? 'border-rose-200/80 bg-rose-50/40'
        : tone === 'violet'
          ? 'border-violet-200/80 bg-violet-50/40'
          : 'border-neutral-200/80 bg-neutral-50/60';
  const iconColor =
    tone === 'emerald'
      ? 'text-emerald-600'
      : tone === 'rose'
        ? 'text-rose-600'
        : tone === 'violet'
          ? 'text-violet-600'
          : 'text-neutral-500';

  return (
    <div className={`rounded-md border px-3 py-2 ${ring}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor} shrink-0`} />
        <div className="min-w-0">
          <p className="text-lg font-bold text-neutral-900 tabular-nums leading-none">{value}</p>
          <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mt-0.5">{label}</p>
          {sub ? <p className="text-[9px] text-neutral-400 mt-0.5">{sub}</p> : null}
        </div>
      </div>
    </div>
  );
}

export default Profile;
